/**
 * Vanilla tab module — replaces Bootstrap 3 tab JS.
 *
 * Trigger markup:
 *   <a href="#paneId" data-tab-toggle>Tab label</a>
 *
 * Expected structure:
 *   <ul class="nav nav-tabs">
 *     <li class="active"><a href="#pane1" data-tab-toggle>…</a></li>
 *     <li><a href="#pane2" data-tab-toggle>…</a></li>
 *   </ul>
 *   <div class="tab-content">
 *     <div class="tab-pane active" id="pane1">…</div>
 *     <div class="tab-pane" id="pane2">…</div>
 *   </div>
 *
 * CSS contract (unchanged from Bootstrap 3):
 *   .tab-content > .tab-pane { display: none; }
 *   .tab-content > .active   { display: block; }
 */

// Guard against double-initialisation when multiple packs import this module
if ((window as any).__tabInitialised) {
  // already running — bail out so listeners aren't registered twice
} else {
(window as any).__tabInitialised = true;

const TOGGLE_ATTR = "data-tab-toggle";
const TOGGLE_SELECTOR = `[${TOGGLE_ATTR}]`;

function activate(trigger: HTMLElement): void {
  const href = trigger.getAttribute("href")?.trim();
  if (!href || !href.startsWith("#")) return;

  const pane = document.querySelector<HTMLElement>(href);
  if (!pane) return;

  const tabContent = pane.parentElement;
  if (!tabContent) return;

  // Deactivate all sibling panes
  tabContent.querySelectorAll<HTMLElement>(":scope > .tab-pane").forEach((p) => {
    p.classList.remove("active", "in");
  });

  // Activate target pane
  pane.classList.add("active", "in");

  // Deactivate all sibling tab links
  const navContainer = trigger.closest<HTMLElement>(".nav");
  if (navContainer) {
    navContainer.querySelectorAll<HTMLElement>(TOGGLE_SELECTOR).forEach((link) => {
      link.classList.remove("active");
      link.closest("li")?.classList.remove("active");
    });
  }

  // Activate clicked tab link
  trigger.classList.add("active");
  trigger.closest("li")?.classList.add("active");
}

// --- listener (delegated on document — works for AJAX-injected content) ---

document.addEventListener("click", (e: MouseEvent) => {
  const trigger = (e.target as HTMLElement).closest<HTMLElement>(TOGGLE_SELECTOR);
  if (!trigger) return;

  e.preventDefault();
  activate(trigger);
});

} // end of double-init guard
